<script setup>
import ContentEditable from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup/ContentEditable.vue";
import {computed, onMounted, ref, defineProps, defineEmits} from "vue";
import InsertSymbolButton from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup/InsertSymbolButton.vue";
import ContentEditableTooltip
  from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup/ContentEditableTooltip.vue";
import NotifyAlert from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup/NotifyAlert.vue";
import {t} from "@/controllerLK/AvalLocalization";

onMounted(() => {
  const userShowChangeQueryNotify = localStorage.getItem("notify_rubrics_change_query");

  if (userShowChangeQueryNotify) {
    isShowChangeQueryNotify.value = JSON.parse(userShowChangeQueryNotify);
  }
});

const props = defineProps({
  rubricQuery: {
    type: String,
    required: true
  },
  searchType: {
    type: Number,
    required: true,
    default: 0
  },
  error: {
    type: Boolean,
    required: true,
    default: false
  },
  errorType: {
    type: [String, null],
    required: true,
    default: null
  },
  isNewRubric: {
    type: Boolean,
    required: true,
  },
  queryMaxLength: {
    type: Number,
    default: 1200
  }
});

const emit = defineEmits(["update:rubric-query"]);

const query = computed({
  get() { return props.rubricQuery; },
  set(val) { emit("update:rubric-query", val); }
});

const contentEditableRef = ref(null);

const handleInsertSymbol = (symbol) => {
  contentEditableRef.value?.insertText(symbol);
};

// Отображение уведомления изменения запроса. Состояние хранится в ls.
const statusNotifyShowChangeQuery = computed(() => {
  return isShowChangeQueryNotify.value && !props.isNewRubric;
});

const isShowChangeQueryNotify = ref(true);

const closeChangeQueryNotify = () => {
  isShowChangeQueryNotify.value = false;
  localStorage.setItem("notify_rubrics_change_query", false);
};
</script>

<template>
  <div class="query-container">
    <label
      for="queryInput"
      class="query-container__label required-symbol"
    >{{ t('news_page_rubrics_edit_popup.input.query_param.title') }}
    </label>
    <span class="query-container__subtitle">
      <template v-if="searchType === 1">
        {{ t('news_page_rubrics_edit_popup.input.query_param.notify_semantic') }}
      </template>

      <template v-else-if="searchType === 0">
        {{ t('news_page_rubrics_edit_popup.input.query_param.notify_classic') }}
      </template>
    </span>

    <div
      class="query-wrapper"
      :class="{'query-wrapper--error': props.error}"
    >
      <div class="query-wrapper__header">
        <InsertSymbolButton
          v-if="props.searchType === 0"
          @insert-symbol="handleInsertSymbol"
        />
        <ContentEditableTooltip />
      </div>

      <ContentEditable
        ref="contentEditableRef"
        v-model="query"
        :max-length="queryMaxLength"
      />
    </div>
    <template v-if="props.error && props.errorType">
      <span
        class="query-container__error-message"
      >
        {{ t(`news_page_rubrics_edit_popup.error.${props.errorType}`) }}
      </span>
    </template>
    <NotifyAlert
      v-if="statusNotifyShowChangeQuery"
      :text="t('news_page_rubrics_edit_popup.alert.query_change')"
      @close-notify="closeChangeQueryNotify"
    />
  </div>
</template>

<style scoped lang="scss">

.query-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  // --background-bg-disabled: в светлой теме = --gray-gray10 (не меняем внешний вид),
  // в тёмной теме — тёмный «утопленный» фон формы вместо непереопределённого белого gray-gray10.
  background-color: var(--background-bg-disabled);
  padding: 10px;
  border-radius: 16px;
  flex: 1;
  min-height: 0;
  position: relative;


  
  &--error {
    border: 1px solid var(--system-red);
  }

  &__header {
    display: flex;
    justify-content: space-between;
  }
}

.query-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;

  &__error-message {
    color: var(--text-text-error);
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
  }

  &__subtitle {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: var(--text-text-gray);
  }

  &__label {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: var(--form-primary-title-text);
  }

  &__alert {
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    color: var(--form-primary-placeholder-text);

    &--error {
      color: var(--system-red);
    }
  }
}
</style>